import QuillEditorViewOnly from "@components/QuillEditor/QuillEditorViewOnly";
import { JobSkills } from "@utils/constants";
import dayjs from "dayjs";
import {
  ClipboardList,
  CodeXml,
  Medal,
  Minus,
  PencilIcon,
  PencilLine,
  Plus,
  ScrollText,
} from "lucide-react";
import { useState } from "react";

const tabs = [
  {
    label: "Summary",
    icon: <ScrollText size={17} />,
    value: 0,
    content: (
      <p>
        Proficiency in HTML/CSS, JavaScript, server languages (Node.js, Python),
        database management (SQL/NoSQL),and API integration, aimed at managing
        the entire web application development lifecycle.
      </p>
    ),
  },
  {
    label: "Skills",
    icon: <CodeXml size={17} />,
    value: 1,
  },
  {
    label: "Key Responsibilities",
    icon: <ClipboardList size={17} />,
    value: 2,
  },
  {
    label: "Key Achievements",
    icon: <Medal size={17} />,
    value: 3,
  },
];

const ExperienceCard = ({ experience,setShowAddEditExperience }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [expandCard, setExpandCard] = useState(false);

  const RenderContent = () => {
    return (
      <div className="px-10 pt-6 pb-12 font-medium leading-5 text-xs text-light-black">
        {tabs?.find((tab) => tab?.value === activeTab)?.content}
      </div>
    );
  };

  return (
    <div className="flex flex-col rounded-md border">
      <div className="flex items-center justify-between p-5">
        <div className="flex items-center gap-5">
          {/* Left border */}
          <div className="w-1 h-14 rounded-full bg-slate-700"></div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-8">
              <span className="font-bold text-slate-700">
                {experience?.jobTitle}
              </span>
              <span className="font-medium text-xs text-light-black">
                {dayjs(experience?.startDate)?.format("MMMM YYYY")} -{" "}
                {experience?.isCurrent
                  ? "Present"
                  : dayjs(experience?.endDate)?.format("MMMM YYYY")}
              </span>
              <button
                onClick={() => {
                  setShowAddEditExperience(experience)
                }}
                className="flex items-center justify-center h-5 w-5 rounded-md hover:bg-gray-100"
              >
                <PencilLine size={14} />
              </button>
            </div>
            <div className="flex items-center gap-5">
              <span className="font-medium text-xs text-light-black">
                {experience?.companyName}
              </span>
              <div className="w-px h-3 bg-light-black" />
              <span className="font-medium text-xs text-light-black">
                {experience?.location}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpandCard(!expandCard)}
          aria-expanded={expandCard}
          className="relative rounded-full flex items-center justify-center h-7 w-7 bg-slate-700"
        >
          <Plus
            size={17}
            color="white"
            className={`absolute transition-all duration-300 ease-out ${expandCard ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"}`}
          />
          <Minus
            size={17}
            color="white"
            className={`absolute transition-all duration-300 ease-out ${expandCard ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"}`}
          />
        </button>
      </div>
      {/* Expanded section */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${expandCard ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden min-h-0">
          <div className="flex flex-col gap-2 border-t bg-blue-50/20">
            {/* Tabs container */}
            <div className="flex items-center px-5 gap-5 border-b-2">
              {tabs?.map((tab) => {
                const isActive = tab?.value === activeTab;
                return (
                  <button
                    key={tab?.value}
                    onClick={() => setActiveTab(tab?.value)}
                    className={`flex flex-col items-center flex-1`}
                  >
                    <div className="flex items-center py-4 gap-2">
                      <span>{tab?.icon}</span>
                      <span
                        className={`font-semibold text-xs ${isActive ? "text-slate-700" : "text-light-black"}`}
                      >
                        {tab?.label}
                      </span>
                    </div>
                    <div
                      className={`w-full h-1 rounded-full transition-colors duration-300 ${isActive ? "bg-slate-700" : "bg-transparent"}`}
                    ></div>
                  </button>
                );
              })}
            </div>

            {/* Render Content */}
            <div className="px-10 pt-6 pb-12 font-medium leading-5 text-xs text-light-black">
              {activeTab === 0 ? (
                <QuillEditorViewOnly value={experience?.jobDescription} />
              ) : activeTab === 1 ? (
                <div className="overflow-auto h-full pr-64">
                  {experience?.skills?.map((skill) => (
                    <div
                      key={skill?._id}
                      className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]"
                    >
                      {[
                        skill.title,
                        `${skill.rating}/10`,
                        skill.years,
                        JobSkills.find((j) => j.value === skill.lastUsed)
                          ?.label || skill.lastUsed,
                      ].map((value, index) => (
                        <div
                          key={index}
                          className="border border-gray-100 bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-charcoal rounded flex flex-1"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;
